import {
  LegacyCase,
  Document,
  DocumentType,
  ProcessingStatus,
  CreateCaseRequest,
  FilterOptions,
} from '../types';
import { caseApiReal, caseApiHelpers } from './caseApiReal';
import { httpClient } from './httpClient';
import { chronologyApi } from './chronologyApi';
import { tokenStorage } from '../utils/tokenStorage';

// Case Management API - Connected to real backend
export const caseApi = {
  // Get all cases
  getCases: async (params?: { 
    status?: string; 
    search?: string; 
    limit?: number; 
    offset?: number; 
  }) => {
    try {
      // Convert frontend params to backend format
      const backendParams = {
        status: params?.status ? caseApiHelpers.mapLegacyStatus(params.status) : undefined,
        search: params?.search,
        page: params?.offset ? Math.floor(params.offset / (params?.limit || 20)) : 0,
        size: params?.limit || 20
      };

      const response = await caseApiReal.getCases(backendParams);
      
      // Convert backend response to frontend format
      const result = {
        cases: response.content.map(caseApiHelpers.toLegacyCase),
        total: response.totalElements,
        limit: response.size,
        offset: response.number * response.size
      };
      return result;
    } catch (error) {
      console.error('❌ Failed to fetch cases from backend:', error);
      throw new Error('Failed to load cases. Please check your connection and try again.');
    }
  },

  // Get case details
  getCase: async (caseId: string): Promise<LegacyCase> => {
    try {
      const backendCase = await caseApiReal.getCase(caseId);
      return caseApiHelpers.toLegacyCase(backendCase);
    } catch (error) {
      console.error('Failed to fetch case from backend:', error);
      throw new Error('Failed to load case details. Please check your connection and try again.');
    }
  },

  // Create new case
  createCase: async (caseData: {
    name: string;
    description?: string;
    patientName?: string;
    caseTypes?: string[];
    incidentDateStart?: string;
    incidentDateEnd?: string;
    includeContextBefore?: boolean;
    includeContextAfter?: boolean;
    processingGuidance?: string;
  }): Promise<LegacyCase> => {
    try {
      const createRequest: CreateCaseRequest = {
        name: caseData.name,
        patientName: caseData.patientName || undefined,
        description: caseData.description,
        priority: 'MEDIUM',
        caseTypes: caseData.caseTypes,
        incidentDateStart: caseData.incidentDateStart,
        incidentDateEnd: caseData.incidentDateEnd,
        includeContextBefore: caseData.includeContextBefore,
        includeContextAfter: caseData.includeContextAfter,
        processingGuidance: caseData.processingGuidance,
      };

      const backendCase = await caseApiReal.createCase(createRequest);
      return caseApiHelpers.toLegacyCase(backendCase);
    } catch (error) {
      console.error('Failed to create case:', error);
      throw new Error('Failed to create case. Please check your input and try again.');
    }
  },

  // Update case
  updateCase: async (caseId: string, updates: Partial<LegacyCase>): Promise<LegacyCase> => {
    try {
      const updateRequest: any = {
        name: updates.name,
        description: updates.description,
        status: updates.status ? caseApiHelpers.mapLegacyStatus(updates.status) : undefined,
      };

      // Phase 2 fields — only include when explicitly provided
      if ('incidentDateStart' in updates) updateRequest.incidentDateStart = updates.incidentDateStart ?? '';
      if ('incidentDateEnd' in updates) updateRequest.incidentDateEnd = updates.incidentDateEnd ?? '';
      if ('processingGuidance' in updates) updateRequest.processingGuidance = updates.processingGuidance ?? '';
      if ('instructionLetterData' in updates) updateRequest.instructionLetterData = updates.instructionLetterData ? JSON.stringify(updates.instructionLetterData) : '';
      if ('instructionLetterDocumentId' in updates) updateRequest.instructionLetterDocumentId = updates.instructionLetterDocumentId ?? '';
      if ('caseTypes' in updates) updateRequest.caseTypes = updates.caseTypes;
      if ('patientName' in updates) updateRequest.patientName = updates.patientName;

      const backendCase = await caseApiReal.updateCase(caseId, updateRequest);
      return caseApiHelpers.toLegacyCase(backendCase);
    } catch (error) {
      console.error('Failed to update case:', error);
      throw new Error('Failed to update case. Please check your input and try again.');
    }
  },

  // Delete case
  deleteCase: async (caseId: string): Promise<void> => {
    try {
      await caseApiReal.deleteCase(caseId);
    } catch (error) {
      console.error('Failed to delete case:', error);
      throw new Error('Failed to delete case. Please try again.');
    }
  },

  // Get case timeline
  getCaseTimeline: async (caseId: string) => {
    try {
      const chronology = await chronologyApi.getChronologyByCaseId(caseId);
      const timeline = chronologyApi.convertToTimeline(chronology);
      return timeline;
    } catch (error) {
      console.error('❌ Failed to get timeline:', error);
      // Return empty timeline if chronology doesn't exist yet
      return {
        caseId,
        events: [],
        gaps: [],
        conflicts: [],
        lastUpdated: new Date().toISOString()
      };
    }
  },
};

// Document Management API
const API_BASE_URL = '/api';

export const documentApi = {
  uploadDocuments: async (caseId: string, files: File[], onProgress?: (progress: number) => void, documentType?: string) => {

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    // Get token for authorization
    const token = tokenStorage.getToken();

    let url = `${API_BASE_URL}/documents/upload?caseId=${caseId}`;
    if (documentType) url += `&documentType=${encodeURIComponent(documentType)}`;
    
    try {
      // Use XMLHttpRequest for better progress tracking
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Track upload progress — cap at 90% during transfer,
        // the remaining 10% represents server-side processing
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const rawProgress = event.loaded / event.total;
            const progress = Math.round(rawProgress * 90);
            onProgress(progress);
          }
        });
        
        xhr.addEventListener('load', () => {
          
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              
              // Check for partial failures (207 Multi-Status or individual errors)
              if (xhr.status === 207 || (Array.isArray(result) && result.some((r: any) => r.status === 'ERROR'))) {
                const errorCount = Array.isArray(result) ? result.filter((r: any) => r.status === 'ERROR').length : 0;
                const successCount = Array.isArray(result) ? result.filter((r: any) => r.status !== 'ERROR').length : 0;
                
                console.warn(`⚠️ Partial upload success: ${successCount} succeeded, ${errorCount} failed`);
                
                // Still resolve with the original array
                resolve(result);
              } else {
                resolve(result);
              }
            } catch (error) {
              console.error('❌ Failed to parse response:', error);
              reject(new Error('Invalid response format'));
            }
          } else if (xhr.status === 413) {
            reject(new Error('Upload too large. Individual files must be under 500MB and total upload under 1GB.'));
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              console.error('❌ Upload failed:', errorData);

              // Extract error details from response if available
              let errorMessage = `Upload failed: ${xhr.statusText}`;
              if (errorData.error === 'UPLOAD_TOO_LARGE') {
                errorMessage = errorData.message;
              } else if (Array.isArray(errorData)) {
                const errors = errorData.filter((r: any) => r.status === 'ERROR');
                if (errors.length > 0) {
                  const reasons = errors
                    .map((r: any) => r.message)
                    .filter(Boolean);
                  errorMessage = reasons.length > 0
                    ? `${errors.length} file(s) failed to upload: ${reasons.join('; ')}`
                    : `${errors.length} file(s) failed to upload`;
                }
              } else if (errorData.message) {
                errorMessage = errorData.message;
              }

              reject(new Error(errorMessage));
            } catch (error) {
              reject(new Error(`Upload failed: ${xhr.statusText}`));
            }
          }
        });
        
        xhr.addEventListener('error', () => {
          console.error('❌ Network error during upload');
          reject(new Error('Network error during upload'));
        });
        
        xhr.addEventListener('timeout', () => {
          console.error('❌ Upload timeout');
          reject(new Error('Upload timeout'));
        });
        
        xhr.open('POST', url);
        xhr.withCredentials = true;
        xhr.timeout = 600000; // 10 minute timeout

        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        
        xhr.send(formData);
      });
    } catch (error) {
      console.error('❌ Upload error:', error);
      throw error;
    }
  },

  getCaseDocuments: async (caseId: string): Promise<Document[]> => {
    try {
      const response = await httpClient.get<any[]>(`/documents/cases/${caseId}/documents`);
      return response.map(documentApi.mapDocumentResponse);
    } catch (error) {
      console.error('❌ Failed to fetch documents:', error);
      throw new Error('Failed to load documents. Please check your connection and try again.');
    }
  },

  /**
   * Synchronously re-extract pageCount on the backend for any documents in the
   * case where it's missing, then return the refreshed list. Used by the Start
   * Analysis credit-confirmation dialog so the user sees accurate deductions.
   */
  recountCaseDocumentPages: async (caseId: string): Promise<Document[]> => {
    const response = await httpClient.post<any[]>(
      `/documents/cases/${caseId}/documents/recount-pages`,
      {},
    );
    return response.map(documentApi.mapDocumentResponse);
  },

  // Map backend document DTO → frontend Document shape
  mapDocumentResponse: (doc: any): Document => {
    const mappedStatus = documentApi.mapProcessingStatus(doc.processingStatus);
    return {
      documentId: doc.id,
      caseId: doc.caseId,
      filename: doc.filename,
      originalPath: doc.originalPath || '',
      fileSize: doc.size,
      pageCount: doc.pageCount || 0,
      blankPageCount: doc.blankPageCount || 0,
      documentType: documentApi.mapDocumentType(doc.documentType),
      uploadDate: doc.uploadedAt,
      processingStatus: mappedStatus,
      ocrResult: doc.ocrConfidence ? {
        documentId: doc.id,
        extractedText: doc.extractedText || '',
        confidence: doc.ocrConfidence,
        pageResults: doc.pageResults || [],
        processingTime: doc.processingTime || 0
      } : undefined,
      textAnalysisStatus: doc.textAnalysisStatus || 'pending',
      integrityStatus: doc.integrityStatus || 'pending',
      lastProcessedAt: doc.lastProcessedAt,
      errorMessage: doc.errorMessage,
      patientIds: doc.patientIds || []
    } as Document;
  },

  // Helper function to map document types
  mapDocumentType: (backendType: string): DocumentType => {
    const typeMap: Record<string, DocumentType> = {
      'CLINIC_LETTER': 'clinic_letter',
      'DISCHARGE_SUMMARY': 'discharge_summary',
      'NURSING_NOTES': 'nursing_notes',
      'INVESTIGATION_REPORT': 'investigation_report',
      'ADMINISTRATIVE': 'administrative',
      'MEDICAL_REPORT': 'other',
      'LAB_RESULTS': 'investigation_report',
      'IMAGING_REPORT': 'investigation_report',
      'PATIENT_RECORD': 'patient_record',
      'GUIDELINE': 'guideline',
      'ACADEMIC_PAPER': 'academic_paper',
      'CORRESPONDENCE': 'correspondence',
      'LEGAL_DOCUMENT': 'legal_document',
      'INSTRUCTION_LETTER': 'instruction_letter',
      'DICOM_IMAGE': 'dicom_image',
      'OTHER': 'other',
      'UNKNOWN': 'unknown'
    };
    return typeMap[backendType?.toUpperCase()] || 'unknown';
  },

  // Helper function to map processing status
  mapProcessingStatus: (backendStatus: string): ProcessingStatus => {
    if (!backendStatus) {
      return 'uploaded';
    }

    const statusMap: Record<string, ProcessingStatus> = {
      'UPLOADED': 'uploaded',
      'UPLOADING': 'uploading',
      'QUEUED': 'queued',
      'PASSWORD_PROTECTED': 'password_protected',
      'SKIPPED_BY_USER': 'skipped_by_user',
      'PROCESSING': 'ocr_processing',
      'READY_FOR_PROCESSING': 'ready_for_processing',
      'OCR_PROCESSING': 'ocr_processing',
      'TEXT_EXTRACTED': 'text_extracted',
      'ANALYZING': 'analyzing',
      'EVENTS_EXTRACTED': 'events_extracted',
      'CHRONOLOGY_READY': 'chronology_ready',
      'COMPLETE': 'complete',
      'PROCESSED': 'complete',
      'ERROR': 'error',
      'FAILED': 'error'
    };

    const key = typeof backendStatus === 'string' ? backendStatus.toUpperCase() : String(backendStatus).toUpperCase();
    return statusMap[key] || 'uploaded';
  },

  getDocument: async (documentId: string): Promise<Document> => {
    return httpClient.get<Document>(`/documents/${documentId}`);
  },

  processDocument: async (documentId: string) => {
    return httpClient.post(`/documents/${documentId}/process`, {});
  },

  deleteDocument: async (documentId: string): Promise<void> => {
    try {
      await httpClient.delete<void>(`/documents/${documentId}`);
    } catch (error) {
      console.error('❌ Document deletion failed:', error);
      throw error;
    }
  },

  /**
   * Attempt to unlock a password-protected PDF. Returns an outcome string so the
   * caller can render per-row feedback without surfacing server internals.
   * The password value is passed once and never retained by this module.
   */
  unlockDocument: async (
    documentId: string,
    password: string
  ): Promise<'success' | 'wrong_password' | 'unsupported_encryption' | 'not_found' | 'error'> => {
    try {
      await httpClient.post<void>(`/documents/${documentId}/unlock`, { password });
      return 'success';
    } catch (error: any) {
      const code: string | undefined = error?.code;
      const reason: string | undefined = error?.details?.reason;
      if (reason === 'wrong_password' || code === 'HTTP_401') return 'wrong_password';
      if (reason === 'unsupported_encryption' || code === 'HTTP_415') return 'unsupported_encryption';
      if (code === 'HTTP_404') return 'not_found';
      return 'error';
    }
  },

  skipDocument: async (documentId: string): Promise<void> => {
    await httpClient.post<void>(`/documents/${documentId}/skip`, {});
  },

  getDocumentFile: async (documentId: string): Promise<Blob | null> => {
    const token = tokenStorage.getToken();
    const url = `${API_BASE_URL}/documents/${documentId}/content`;
    
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf, application/octet-stream, */*'
        },
        credentials: 'include',
      });


      if (!response.ok) {
        console.error('❌ Failed to get document file:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ Error response body:', errorText);
        return null;
      }

      // Get the blob
      const blob = await response.blob();
      return blob;
      
    } catch (error) {
      console.error('❌ Error getting document file:', error);
      return null;
    }
  },

  viewDocument: async (documentId: string): Promise<void> => {
    const token = tokenStorage.getToken();
    const url = `${API_BASE_URL}/documents/${documentId}/content`;
    
    try {
      // Fetch the document with authentication
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`);
      }

      // Get the blob and content type
      const blob = await response.blob();
      
      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
        }
      }

      // Create a blob URL and open it in a new tab
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Clean up the blob URL after a delay (to allow the browser to load it)
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);

    } catch (error) {
      console.error('❌ Failed to view document:', error);
      throw error;
    }
  },

  // Enhanced document processing APIs
  getDocumentTextAnalysis: async (documentId: string) => {
    try {
      const response = await httpClient.get(`/documents/${documentId}/text-analysis`);
      return response;
    } catch (error) {
      console.error('❌ Failed to get text analysis:', error);
      throw error;
    }
  },

  analyzeDocumentText: async (documentId: string) => {
    try {
      const response = await httpClient.post(`/documents/${documentId}/analyze-text`);
      return response;
    } catch (error) {
      console.error('❌ Failed to analyze document text:', error);
      throw error;
    }
  },

  validateDocumentIntegrity: async (documentId: string) => {
    try {
      const response = await httpClient.post(`/documents/${documentId}/validate-integrity`);
      return response;
    } catch (error) {
      console.error('❌ Failed to validate document integrity:', error);
      throw error;
    }
  },

  getDetailedProcessingStatus: async (documentId: string) => {
    try {
      const response = await httpClient.get(`/documents/${documentId}/processing-status`);
      return response;
    } catch (error) {
      console.error('❌ Failed to get processing status:', error);
      throw error;
    }
  },

  // WebSocket connection for real-time progress updates
  subscribeToProcessingProgress: (documentId: string, onProgress: (progress: any) => void) => {
    const token = tokenStorage.getToken();
    const url = `${API_BASE_URL}/documents/upload-progress/${documentId}`;
    
    const eventSource = new EventSource(url + (token ? `?token=${token}` : ''));
    
    eventSource.onmessage = (event) => {
      try {
        const progressData = JSON.parse(event.data);
        onProgress(progressData);
      } catch (error) {
        console.error('❌ Failed to parse progress data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('❌ EventSource error:', error);
      eventSource.close();
    };

    return eventSource;
  },

  // DICOM API methods
  uploadDicomStudy: async (caseId: string, files: File[], onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const token = tokenStorage.getToken();
    const url = `${API_BASE_URL}/documents/dicom/upload?caseId=${caseId}`;

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 90);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error('Invalid response format'));
          }
        } else {
          let errorMessage = `DICOM upload failed: ${xhr.statusText}`;
          try {
            const errorData = JSON.parse(xhr.responseText);
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch { /* use default message */ }
          reject(new Error(errorMessage));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error during DICOM upload')));
      xhr.addEventListener('timeout', () => reject(new Error('DICOM upload timeout')));

      xhr.open('POST', url);
      xhr.withCredentials = true;
      xhr.timeout = 600000; // 10 minute timeout for large series
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    });
  },

  getDicomStudies: async (caseId: string) => {
    return httpClient.get<any[]>(`/documents/dicom/cases/${caseId}/studies`);
  },

  getDicomStudy: async (studyId: string) => {
    return httpClient.get<any>(`/documents/dicom/studies/${studyId}`);
  },

  getDicomInstanceUrl: (studyId: string, instanceId: string): string => {
    return `${API_BASE_URL}/documents/dicom/wado/${studyId}/instances/${instanceId}`;
  },

  getDicomThumbnailUrl: (studyId: string): string => {
    return `${API_BASE_URL}/documents/dicom/studies/${studyId}/thumbnail`;
  },

  deleteDicomStudy: async (studyId: string) => {
    return httpClient.delete(`/documents/dicom/studies/${studyId}`);
  },

  /**
   * Get pre-stored Textract line geometry for a specific page.
   * Returns an array of line bounding boxes, or null if none stored.
   */
  getLineGeometry: async (documentId: string, pageNumber: number): Promise<Array<{ text: string; left: number; top: number; width: number; height: number }> | null> => {
    try {
      const response = await httpClient.get<Array<{ text: string; left: number; top: number; width: number; height: number }>>(
        `/documents/${documentId}/line-geometry?page=${pageNumber}`
      );
      return response && response.length > 0 ? response : null;
    } catch {
      return null;
    }
  },

  /**
   * Get per-page rotation corrections detected by Tesseract OSD during OCR.
   * Returns a map of {pageNumber: rotationDegrees}, or null if none stored.
   */
  getPageRotations: async (documentId: string): Promise<Record<number, number> | null> => {
    try {
      const response = await httpClient.get<Record<string, number>>(
        `/documents/${documentId}/page-rotations`
      );
      if (!response || Object.keys(response).length === 0) return null;
      const result: Record<number, number> = {};
      for (const [page, rotation] of Object.entries(response)) {
        result[Number(page)] = rotation;
      }
      return result;
    } catch {
      return null;
    }
  },

  /**
   * Get Haiku-detected PII values for a document (for PDF redaction).
   * Returns an array of PII strings, or null if not available.
   */
  getDetectedPii: async (documentId: string): Promise<string[] | null> => {
    try {
      const response = await httpClient.get<string[]>(
        `/documents/${documentId}/detected-pii`
      );
      return response && response.length > 0 ? response : null;
    } catch {
      return null;
    }
  },
};

// Timeline API - Now integrated with chronology service
export const timelineApi = {
  getTimeline: async (caseId: string, _params?: FilterOptions) => {
    try {
      const chronology = await chronologyApi.getChronologyByCaseId(caseId, 'REVIEW');
      const timeline = chronologyApi.convertToTimeline(chronology);
      return { timeline, chronologyId: chronology.id || undefined };
    } catch (error) {
      console.error('❌ Failed to get timeline:', error);
      // Return empty timeline if chronology doesn't exist yet
      return {
        timeline: {
          caseId,
          events: [],
          gaps: [],
          conflicts: [],
          lastUpdated: new Date().toISOString()
        },
        chronologyId: undefined
      };
    }
  },

  getTimelineEvents: async (caseId: string, _params?: {
    eventType?: string;
    startDate?: string;
    endDate?: string;
    certainty?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      const chronology = await chronologyApi.getChronologyByCaseId(caseId, 'REVIEW');
      const timeline = chronologyApi.convertToTimeline(chronology);
      return timeline.events;
    } catch (error) {
      console.error('❌ Failed to get timeline events:', error);
      return [];
    }
  },
};

// Evidence Lanes API - Not yet implemented
export const evidenceApi = {
  getEvidenceLanes: async (_caseId: string, _params?: { showContradictions?: boolean }) => {
    throw new Error('Evidence service not yet implemented');
  },

  getEvidenceLane: async (_caseId: string, _laneType: string, _params?: {
    startDate?: string;
    endDate?: string;
    showContradictions?: boolean;
  }) => {
    throw new Error('Evidence service not yet implemented');
  },

  searchEvidence: async (_caseId: string, _query: string, _params?: {
    lanes?: string[];
    limit?: number;
  }) => {
    throw new Error('Evidence service not yet implemented');
  },
};

// Query Processing API - Not yet implemented
export const queryApi = {
  processQuery: async (_caseId: string, _question: string) => {
    throw new Error('Query service not yet implemented');
  },

  getQuerySuggestions: async (_caseId: string) => {
    throw new Error('Query service not yet implemented');
  },
};

// Case Stats API - Not yet implemented
export const statsApi = {
  getCaseStats: async (_caseId: string) => {
    throw new Error('Stats service not yet implemented');
  },
};
